import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Bot, Clock, TrendingUp, Zap, ArrowRight, Target } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";

interface MatchInfo {
  home_team: string;
  away_team: string;
  league: string;
  match_date: string;
}

interface AIMatchPrediction {
  id: string;
  match_info: MatchInfo | any;
  title: string;
  description: string;
  odds: number;
  confidence_level: number;
  sport: string;
  ai_persona_id: string;
  persona?: {
    name: string;
    avatar_url: string;
  };
}

export function AIMatchFeed() {
  const [predictions, setPredictions] = useState<AIMatchPrediction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAIPredictions();
    
    // Auto-refresh every 6 hours
    const interval = setInterval(() => {
      fetchAIPredictions();
    }, 6 * 60 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchAIPredictions = async () => {
    try {
      const { data, error } = await supabase
        .from('enhanced_tips')
        .select(`
          *,
          ai_tipster_personas!inner (
            name,
            avatar_url
          )
        `)
        .eq('is_ai_generated', true)
        .eq('status', 'pending')
        .gte('expires_at', new Date().toISOString())
        .order('confidence_level', { ascending: false })
        .limit(6);

      if (error) throw error;
      
      const mapped = data?.map(tip => ({
        id: tip.id,
        match_info: tip.match_info as unknown as MatchInfo,
        title: tip.title,
        description: tip.description || '',
        odds: tip.odds,
        confidence_level: tip.confidence_level || 0,
        sport: tip.sport,
        ai_persona_id: tip.ai_persona_id || '',
        persona: tip.ai_tipster_personas as { name: string; avatar_url: string }
      })) || [];
      
      setPredictions(mapped);
    } catch (error) {
      console.error('Error fetching AI predictions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatMatchTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Starting soon';
    if (diffHours < 24) return `In ${diffHours}h`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return 'text-profit';
    if (confidence >= 60) return 'text-warning';
    return 'text-muted-foreground';
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-purple-600" />
            AI Match Predictions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse">
                <div className="h-20 bg-muted rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (predictions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-purple-600" />
            AI Match Predictions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 space-y-4">
            <div className="relative">
              <Bot className="w-16 h-16 mx-auto text-purple-500/50 animate-pulse" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin" />
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-1">Generating Predictions</h4>
              <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                Our AI analysts are crunching the latest odds and team data. Check back shortly!
              </p>
            </div>
            <div className="flex justify-center gap-2 text-xs text-muted-foreground">
              <Badge variant="outline" className="text-xs">
                <Zap className="w-3 h-3 mr-1" />
                Auto-refresh enabled
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-purple-600" />
            AI Match Predictions
            <Badge variant="secondary" className="ml-2">
              Live
            </Badge>
          </CardTitle>
          <Link to="/tips">
            <Button variant="ghost" size="sm" className="gap-1">
              View All
              <ArrowRight className="w-3 h-3" />
            </Button>
          </Link>
        </div>
        <p className="text-sm text-muted-foreground">
          Generated by AI personas analyzing odds, sentiment, and team dynamics
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {predictions.map((prediction, index) => (
            <div key={prediction.id}>
              <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                <div className="flex-shrink-0">
                  {prediction.persona?.avatar_url ? (
                    <img 
                      src={prediction.persona.avatar_url} 
                      alt={prediction.persona.name}
                      className="w-10 h-10 rounded-full border-2 border-purple-500/20"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                      <Bot className="w-5 h-5 text-purple-600" />
                    </div>
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex-1">
                      <p className="text-sm font-medium leading-tight">
                        {prediction.match_info.home_team} vs {prediction.match_info.away_team}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {prediction.match_info.league}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {prediction.odds}x
                    </Badge>
                  </div>
                  
                  <div className="flex items-center gap-2 text-xs mb-2">
                    <Clock className="w-3 h-3 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      {formatMatchTime(prediction.match_info.match_date)}
                    </span>
                    <Separator orientation="vertical" className="h-3" />
                    <Target className={`w-3 h-3 ${getConfidenceColor(prediction.confidence_level)}`} />
                    <span className={getConfidenceColor(prediction.confidence_level)}>
                      {prediction.confidence_level}% confidence
                    </span>
                  </div>
                  
                  <p className="text-xs text-muted-foreground italic line-clamp-2">
                    "{prediction.description}"
                  </p>
                  
                  <div className="flex items-center gap-1 mt-2">
                    <span className="text-xs text-purple-600 font-medium">
                      {prediction.persona?.name}
                    </span>
                  </div>
                </div>
              </div>
              
              {index < predictions.length - 1 && <Separator className="my-3" />}
            </div>
          ))}
        </div>
        
        <div className="mt-4 pt-4 border-t">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Zap className="w-3 h-3" />
              Auto-refreshes every 6 hours
            </span>
            <Link to="/tips">
              <Button variant="link" size="sm" className="h-auto p-0 text-xs">
                Explore Full Archive →
              </Button>
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}